import { format } from 'prettier/standalone';
import type { Plugin } from 'prettier';
import * as prettierPluginHtml from 'prettier/plugins/html';
import * as prettierPluginMarkdown from 'prettier/plugins/markdown';
import * as prettierPluginBabel from 'prettier/plugins/babel';
import * as prettierPluginEstree from 'prettier/plugins/estree';
import * as prettierPluginTypescript from 'prettier/plugins/typescript';
import * as prettierPluginYaml from 'prettier/plugins/yaml';
import * as prettierPluginPostcss from 'prettier/plugins/postcss';
import type { IndentStyle } from '../types';
import { getIndentWidth } from './indentation';

export type FormatterLanguage =
  | 'html'
  | 'markdown'
  | 'javascript'
  | 'typescript'
  | 'json'
  | 'css'
  | 'yaml';

interface FormatterConfig {
  parser: string;
  plugins: Plugin[];
  options?: Record<string, unknown>;
}

const FORMATTERS: Record<FormatterLanguage, FormatterConfig> = {
  html: {
    parser: 'html',
    plugins: [prettierPluginHtml as Plugin],
    options: { htmlWhitespaceSensitivity: 'ignore' },
  },
  markdown: {
    parser: 'markdown',
    plugins: [prettierPluginMarkdown as Plugin],
  },
  javascript: {
    parser: 'babel',
    plugins: [prettierPluginBabel as Plugin, prettierPluginEstree as Plugin],
  },
  typescript: {
    parser: 'typescript',
    plugins: [prettierPluginTypescript as Plugin, prettierPluginEstree as Plugin],
  },
  json: {
    parser: 'json',
    plugins: [prettierPluginBabel as Plugin, prettierPluginEstree as Plugin],
  },
  css: {
    parser: 'css',
    plugins: [prettierPluginPostcss as Plugin],
  },
  yaml: {
    parser: 'yaml',
    plugins: [prettierPluginYaml as Plugin],
  },
};

export function hasFormatter(language: string): language is FormatterLanguage {
  return language in FORMATTERS;
}

export interface FormatOptions {
  indentStyle?: IndentStyle;
}

export async function formatContent(
  content: string,
  language: FormatterLanguage,
  options?: FormatOptions
): Promise<string> {
  if (!content.trim()) return content;

  const config = FORMATTERS[language];
  if (!config) return content;

  // Determine tab settings based on indent style
  const indentStyle = options?.indentStyle ?? 'tab';
  const useTabs = indentStyle === 'tab';
  const tabWidth = indentStyle === 'tab' ? 2 : getIndentWidth(indentStyle);

  try {
    return await format(content, {
      parser: config.parser,
      plugins: config.plugins,
      printWidth: 80,
      tabWidth,
      useTabs,
      ...config.options,
    });
  } catch {
    // Return original content on parse error
    return content;
  }
}
